import { useEffect, useState, useCallback, useRef } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UploadSimple, FileText, Trash, ArrowsClockwise, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

function MediaThumb({ item, token }) {
  if (item.content_type?.startsWith("image/") && item.thumb_path) {
    return <img data-testid="media-thumb" src={`${API}/media/${item.id}/thumbnail?auth=${token}`} alt={item.original_filename}
      className="h-40 w-full rounded-t-md object-cover" loading="lazy" />;
  }
  return <div className="flex h-40 w-full items-center justify-center rounded-t-md bg-muted"><FileText size={48} weight="duotone" className="text-muted-foreground" /></div>;
}

export default function Media() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const verRef = useRef(null);
  const [verId, setVerId] = useState(null);
  const token = localStorage.getItem("csp_token");

  const load = useCallback(() => api.get("/media").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "Allgemein");
    try { await api.post("/media/upload", fd); toast.success("Datei hochgeladen", { description: file.name }); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const uploadVersion = async (file) => {
    if (!file || !verId) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/media/${verId}/version`, fd); toast.success("Neue Version gespeichert"); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setVerId(null); if (verRef.current) verRef.current.value = ""; }
  };

  const del = async (id) => { await api.delete(`/media/${id}`); toast.success("Datei gelöscht"); load(); };
  const view = (id) => window.open(`${API}/media/${id}/download?auth=${token}`, "_blank");

  if (!items) return <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Medien" subtitle={`${items.length} Dateien · Objektspeicher · Versionierung · Thumbnails`}>
        <input ref={fileRef} type="file" data-testid="media-file-input" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <Button data-testid="media-upload-button" className="gap-2" disabled={uploading} onClick={() => fileRef.current?.click()}>
          <UploadSimple size={18} weight="bold" />{uploading ? "Lädt hoch…" : "Datei hochladen"}
        </Button>
        <input ref={verRef} type="file" className="hidden" onChange={(e) => uploadVersion(e.target.files?.[0])} />
      </PageHeader>

      {items.length === 0 && <Card className="rounded-md border border-dashed p-12 text-center text-sm text-muted-foreground">Noch keine Medien. Laden Sie Fotos oder Dokumente hoch.</Card>}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((m) => (
          <Card key={m.id} data-testid="media-card" className="overflow-hidden rounded-md border transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm">
            <MediaThumb item={m} token={token} />
            <div className="p-3">
              <div className="truncate text-sm font-semibold" title={m.original_filename}>{m.original_filename}</div>
              <div className="mt-1 flex items-center justify-between">
                <StatusBadge tone="blue">v{m.version}</StatusBadge>
                <span className="text-xs text-muted-foreground">{(m.size / 1024).toFixed(0)} KB</span>
              </div>
              <div className="mt-2 flex gap-1">
                <Button size="sm" variant="ghost" className="h-8 flex-1 gap-1" onClick={() => view(m.id)}><Eye size={15} />Ansehen</Button>
                <Button size="sm" variant="ghost" className="h-8" title="Neue Version" onClick={() => { setVerId(m.id); setTimeout(() => verRef.current?.click(), 50); }}><ArrowsClockwise size={15} /></Button>
                <Button size="sm" variant="ghost" className="h-8" data-testid={`media-delete-${m.id}`} onClick={() => del(m.id)}><Trash size={15} /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
